import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button } from '@material-ui/core';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import Utils from '@/common/utils/utils';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { PRECONDITION_ERROR_CODES, ZIP_ERROR_CODES } from '@/common/constant/McpBootstrapConstant';
import LoadingPanel from '@/component/common/LoadingPanel';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { LOCAL_MCP_PROJECT_PREFIX } from '@/component/mcpCatalog/McpCatalogConstant';
import { generateMCPBootstrap, downloadMCPBootstrap, getMCPBootstrapStatus } from '@/service/mcpBootstrapService';
import '../MCPServerSetting.css';
import DownloadIcon from '@/component/common/icons/DownloadIcon';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

const resolveErrorMessage = errorCode => {
    if (PRECONDITION_ERROR_CODES.includes(errorCode)) {
        return trI18n('COMMSG030');
    }
    if (ZIP_ERROR_CODES.includes(errorCode)) {
        return trI18n('MCP_LOCAL_DOWNLOAD_ERROR');
    }
    return trI18n('MCP_LOCAL_CONNECTION_ERROR');
};

const formatDateTime = value => {
    if (Utils.isEmptyString(value)) return '-';
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY/MM/DD HH:mm') : '-';
};

ProjectLocalMcpTab.propTypes = {
    setIsLoading: PropTypes.func,
    corporationDetail: PropTypes.object,
    projectInuseDetail: PropTypes.object,
    userDetail: PropTypes.object
};

function ProjectLocalMcpTab({ setIsLoading, corporationDetail, projectInuseDetail, userDetail }) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const [isLocalLoading, setIsLocalLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [mcpStatus, setMcpStatus] = useState(null);

    const corporationId = corporationDetail?.id;
    const projectId = projectInuseDetail?.id;

    const bootstrapVersion = mcpStatus?.bootstrapVersion || null;
    const downloadedAt = mcpStatus?.downloadedAt || null;
    const latestVersion = mcpStatus?.latestVersion || null;
    const latestReleaseDate = mcpStatus?.latestReleaseDate || null;

    const isDownloaded = !Utils.isEmptyString(bootstrapVersion);

    const showUpdateNotice = !!(
        (mcpStatus?.hasUpdate || mcpStatus?.hasMcpCatalogUpdate) &&
        isDownloaded &&
        latestVersion
    );

    const loadMcpStatus = useCallback(async () => {
        const result = await getMCPBootstrapStatus(corporationId, projectId, LOCAL_MCP_PROJECT_PREFIX);

        if (!result?.eligible) {
            setErrorMessage(result?.errorCode
                ? resolveErrorMessage(result?.errorCode)
                : trI18n('COMMSG030')
            );
            return;
        }

        setMcpStatus(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [corporationId, projectId]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setIsLocalLoading(true);
        setIsLoading(true);
        loadMcpStatus()
            .catch(error => {
                console.log('loadMcpStatus error >>>', error);
            })
            .finally(() => {
                setIsLocalLoading(false);
                setIsLoading(false);
            });
    }, [loadMcpStatus, setIsLoading]);

    const handleDownload = async () => {
        if (isLocalLoading) return;
        setIsLocalLoading(true);
        try {
            const result = await generateMCPBootstrap(corporationId, projectId, LOCAL_MCP_PROJECT_PREFIX);
            if (!result?.success) {
                setErrorMessage(resolveErrorMessage(result?.errorCode));
                return;
            }

            const corpDomain = localStorageUtil.getLastLoginCorpDomain();
            downloadMCPBootstrap(
                result.downloadUrl,
                `bootstrap_project_${corpDomain}.zip`
            );
            await loadMcpStatus();
        } catch (err) {
            setErrorMessage(resolveErrorMessage(null));
        } finally {
            setIsLocalLoading(false);
        }
    };

    return (
        <div className="project-local-mcp-tab">
            <LoadingPanel isLoading={isLocalLoading} />
            <h3 className="project-local-mcp-tab-title">
                {trI18n('MCP_SERVER_SETTING_PROJECT_LOCAL_MCP_TITLE')}
            </h3>
            <div className="project-local-mcp-tab-content">
                {isDownloaded && (
                    <>
                        <div className="project-local-mcp-version-row">
                            <span className="project-local-mcp-label">
                                {trI18n('MCP_SERVER_SETTING_CURRENT_VERSION')}
                            </span>
                            <span className="project-local-mcp-value">{`v${bootstrapVersion}`}</span>
                        </div>
                        <div className="project-local-mcp-version-row">
                            <span className="project-local-mcp-label">
                                {trI18n('MCP_SERVER_SETTING_INSTALLED_DATE')}
                            </span>
                            <span className="project-local-mcp-value">{formatDateTime(downloadedAt)}</span>
                        </div>
                    </>
                )}

                {showUpdateNotice && (
                    <div className="project-local-mcp-update-block">
                        <div className="project-local-mcp-version-row">
                            <span className="project-local-mcp-label">
                                {trI18n('MCP_LOCAL_UPDATE_DATE')}
                            </span>
                            <span className="project-local-mcp-value">{formatDateTime(latestReleaseDate)}</span>
                        </div>
                        <div className="project-local-mcp-version-row">
                            <span className="project-local-mcp-label">
                                {trI18n('MCP_LOCAL_LATEST_VERSION')}
                            </span>
                            <span className="project-local-mcp-value">{`v${latestVersion}`}</span>
                        </div>
                        <div className="project-local-mcp-update-notice">
                            <WarningRoundedIcon className="project-local-mcp-update-notice-icon" />
                            <span>{trI18n('MCP_LOCAL_UPDATE_AVAILABLE')}</span>
                        </div>
                    </div>
                )}

                <div className="project-local-mcp-actions">
                    <Button
                        variant="contained"
                        color="primary"
                        className="project-local-mcp-download-btn"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={isLocalLoading}
                    >
                        {trI18n('MCP_LOCAL_DOWNLOAD_BTN')}
                    </Button>
                </div>
            </div>

            <ConfirmDialog
                isOpen={!Utils.isEmptyString(errorMessage)}
                msgConfirm={errorMessage}
                okCallBack={() => window.location.reload()}
                textSubmit={trI18n('CM-BUTTON-OK')}
            />
        </div>
    );
}

export default ProjectLocalMcpTab;
